import React, { useState, type SyntheticEvent } from 'react';
import '../styles/sections/WorkExperienceSection.css';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Fade } from 'react-awesome-reveal';

const isMobile = window.innerWidth < 900;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  className?: string;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  className,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={className}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  if (isMobile) {
    return {
      id: `full-width-tab-${index}`,
      'aria-controls': `full-width-tabpanel-${index}`,
    };
  }
  return {
    id: `vertical-tab-${index}`,
  };
};

interface JobInfo {
  role: string;
  time: string;
  workdone: string[];
}

interface JobsData {
  [key: string]: JobInfo;
}

export const WorkExperienceSection: React.FC = () => {
  const jobs: JobsData = {
    'BMO Capital Markets': {
      role: 'Quantitative Analyst - Cross Asset Derivatives',
      time: 'December 2023 – August 2024',
      workdone: [
        'Implemented and deployed a full-stack Performance Attribution Analysis application utilizing PostgreSQL, Flask (Python), and React.js used by Canadian and US based teams',
        'Produced daily sensitivity reports for existing product baskets to assess their sensitivity to multiple market shock scenarios',
        'Improved and automated the quarterly production of a comprehensive report on option risk hedges\' performance using VBA',
        'Contributed to the design of new database pipelines and clustering methods, optimizing data processing for trading floor teams, resulting in a 40% improvement in average query speed',
      ],
    },
    'TMK Group': {
      role: 'Summer Intern - Software and Testing Team',
      time: 'June 2022 – August 2022',
      workdone: [
        'Optimized Python simulation scripts resulting in a 10% decrease in computation time while allowing better interactivity for the engineering and design teams during all testing phases',
        'Remodelled oil and natural gas pipeline threaded connections in the Abaqus FEA to allow for more efficient stress testing simulations',
        'Co-drafted a complete documentation of the simulation scripts used in the testing process',
      ],
    },
    'Sberbank CIB': {
      role: 'Data Science Intern',
      time: 'June 2021 – August 2021',
      workdone: [
        'Built credit risk models using logistic regression, random forest, and XGBoost to improve default prediction accuracy',
        'Developed a Python package for data cleaning interpretation, streamlining data preprocessing and enhancing analysis efficiency',
        'Collaborated with the data engineering team to deploy machine learning models into production, enabling team-wide utilization',
      ],
    },
  };

  const jobsMobile: JobsData = {
    'BMO - CM': {
      role: 'Quant Dev',
      time: 'Winter - Summer 2024',
      workdone: [
        'Implemented and deployed a full-stack Performance Attribution Analysis application used by Canadian and US based teams',
        'Produced daily sensitivity reports for existing product baskets to assess their sensitivity to multiple market shock scenarios',
        'Contributed to the design of new database pipelines and clustering methods, optimizing data processing for trading floor teams',
      ],
    },
    'TMK Group': {
      role: 'SWE Intern',
      time: 'Summer 2022',
      workdone: [
        'Improved existing Python simulation scripts to allow better interactivity for the engineering and design team during all phases of testing',
        'Remodeled existing oil and natural gas pipeline threaded connections using the Abaqus FEA to allow more efficient stress testing simulations',
        'Co-drafted a complete documentation of the simulation scripts used in the testing process',
      ],
    },
    'Sberbank CIB': {
      role: 'DS Intern',
      time: 'Summer 2021',
      workdone: [
        'Built credit risk models using logistic regression, random forest, and XGBoost to improve default prediction accuracy',
        'Developed a Python package for data cleaning interpretation, streamlining data preprocessing and enhancing analysis efficiency',
        'Collaborated with the data engineering team to deploy machine learning models into production, enabling team-wide utilization',
      ],
    },
  };

  const [value, setValue] = useState(0);

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const currentJobs = isMobile ? jobsMobile : jobs;

  return (
    <section className="workBackground">
      <div className="workContent">
        <div className="workSectionTitle">
          <h1 className="sectionNumber">02.</h1>
          <h2 className="sectionTitle">Work Experience</h2>
        </div>
        <div className="workSelector">
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: 'yellowgreen',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Tabs
              TabIndicatorProps={{
                sx: {
                  backgroundColor: 'black',
                },
              }}
              orientation="vertical"
              variant={isMobile ? 'fullWidth' : 'scrollable'}
              value={value}
              onChange={handleChange}
              textColor="inherit"
              sx={{
                borderRight: 1,
                borderColor: 'divider',
                minWidth: '160px',
              }}
            >
              {Object.keys(currentJobs).map((key, i) => (
                <Tab key={key} label={key} {...a11yProps(i)} />
              ))}
            </Tabs>
            {Object.keys(currentJobs).map((key, i) => (
              <TabPanel
                key={key}
                className="workSectionJobBlock"
                value={value}
                index={i}
              >
                <span className="workSectionJobTitle">
                  {currentJobs[key].role + ' @ ' + key}
                </span>
                <div className="workSectionJobTime">{currentJobs[key].time}</div>
                <ul>
                  {currentJobs[key].workdone.map((item, j) => (
                    <Fade
                      key={j}
                      direction={isMobile ? 'up' : 'left'}
                      delay={100 + j * (isMobile ? 100 : 150)}
                    >
                      <li>{item}</li>
                    </Fade>
                  ))}
                </ul>
              </TabPanel>
            ))}
          </Box>
        </div>
      </div>
    </section>
  );
};
